import { useMemo } from 'react';
import debounce from 'lodash/debounce';
import { WatchQueryFetchPolicy } from '@apollo/client';

import { useLoggingMutation, useLoggingQuery } from 'lib/apollo/apollo-hooks';
import {
  GET_MY_ANSWERS,
  GET_CLASS_STUDENT_ANSWERS,
  UPSERT_TEACHER_ACTIVITY_ANSWER,
} from './activity-answers.graphql';
import type {
  UpsertTeacherActivityAnswerMutation,
  UpsertTeacherActivityAnswerMutationVariables,
} from './activity-answers.graphql.generated';
import type { UpsertTeacherActivityAnswerInput } from '../../../gql/schema-types';
import customNanoId from 'lib/nanoid/nanoid';
import { useGetInteractiveLessonParams } from '../activity-builder.hooks';
import type {
  GetMyAnswersQuery,
  GetMyAnswersQueryVariables,
} from './activity-answers.graphql.generated';
import type {
  GetClassStudentAnswersQuery,
  GetClassStudentAnswersQueryVariables,
} from './activity-answers.graphql.generated';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';

type AnswerInput = Omit<
  UpsertTeacherActivityAnswerInput,
  'contentId' | 'contentType' | 'contentVersion' | 'classId'
>;

export const STUDENT_ANSWER_POLL_INTERVAL = 5000;

export const useAnswerMutations = (props?: {
  onCompleted?: (data: UpsertTeacherActivityAnswerMutation) => void;
}) => {
  const { contentId, contentType, version, userClassId } =
    useGetInteractiveLessonParams();

  const [upsertAnswer, { loading, error }] = useLoggingMutation<
    UpsertTeacherActivityAnswerMutation,
    UpsertTeacherActivityAnswerMutationVariables
  >(UPSERT_TEACHER_ACTIVITY_ANSWER, {
    onCompleted: res => {
      props?.onCompleted?.(res);
    },
  });

  const debounceUpsertAnswer = useMemo(
    () =>
      debounce((input: AnswerInput) => {
        if (version && userClassId) {
          upsertAnswer({
            variables: {
              input: {
                ...input,
                classId: Number(userClassId),
                contentType,
                contentId,
                contentVersion: version,
              },
            },
          });
        }
      }, 300),
    //eslint-disable-next-line
    []
  );

  return {
    loading,
    error,
    debounceUpsertAnswer,
  };
};

export const useActivityAnswerId = (answer?: null | { uuid: string }) => {
  const id = useMemo(
    () => (answer?.uuid ? answer?.uuid : customNanoId()),
    [answer?.uuid]
  );

  return id;
};

export const useTeacherGetAnswers = ({
  skip,
  fetchPolicy,
  componentId,
  onCompleted,
}: {
  skip?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
  componentId?: string;
  onCompleted?: (data: GetMyAnswersQuery) => void;
}) => {
  const { contentId, contentType, version, userClassId } =
    useGetInteractiveLessonParams();

  const { showError } = useSnackbar();

  const { data, loading, variables, error, client } = useLoggingQuery<
    GetMyAnswersQuery,
    GetMyAnswersQueryVariables
  >(
    GET_MY_ANSWERS,
    {
      fetchPolicy,
      skip: !version || !userClassId || skip,
      variables: {
        input: {
          contentId,
          contentType,
          classId: Number(userClassId),
          contentVersion: version!,
          componentId,
        },
      },
      onCompleted: res => {
        onCompleted?.(res);
      },
      onError: () => {
        showError(
          'There was an error loading your answers. Please refresh and try again.',
          {
            autoHide: false,
          }
        );
      },
    },
    true
  );

  return {
    data,
    loading,
    error,
    variables,
    hasFetchedAnswers: !!(error || data?.getMyAnswers),
    client,
    componentAnswers: data?.getMyAnswers.filter(
      a => a.componentId === componentId
    ),
  };
};

export const useGetStudentAnswers = ({
  studentId,
  classId,
  fetchPolicy,
  pollInterval,
  componentId,
  skip,
}: {
  studentId?: number | null;
  classId: number | null;
  componentId?: string | null;
  fetchPolicy?: WatchQueryFetchPolicy;
  pollInterval?: number;
  skip?: boolean;
}) => {
  const { contentId, contentType, version } = useGetInteractiveLessonParams();

  const { data, loading, error } = useLoggingQuery<
    GetClassStudentAnswersQuery,
    GetClassStudentAnswersQueryVariables
  >(GET_CLASS_STUDENT_ANSWERS, {
    skip: !version || !classId || skip,
    fetchPolicy,
    pollInterval: pollInterval || 0,
    variables: {
      input: {
        classId: classId || 1,
        contentId,
        contentType,
        contentVersion: version!,
        studentId,
        componentId,
        loadDrawingData: false,
      },
    },
  });

  return {
    data,
    loading,
    error,
  };
};
